import * as React from 'react'
import {
  type ColumnDef,
  type ColumnPinningState,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
  type Header,
  type Cell,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  closestCenter,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronUp, ChevronDown, ChevronsUpDown, Settings, RotateCcw, Pin, PinOff } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { DashboardRow, InventoryLocation } from '@/hooks/useDashboard'
import { signalKind, SIGNAL_LABELS, useInventoryLocations } from '@/hooks/useDashboard'
import { useDashboardStore } from '@/store/dashboardStore'
import { cn } from '@/lib/utils'
import { fmtUsualPace, fmtVsUsual } from '@/lib/lifetimeFormat'

// ── Shopify product link ─────────────────────────────────────────────────────
//
// VITE_SHOPIFY_SHOP is read at build time. If unset, the title renders as
// plain text (no link). Format: dapper-horse.myshopify.com (no scheme).
const SHOPIFY_SHOP = (import.meta.env.VITE_SHOPIFY_SHOP as string | undefined)?.trim() || ''

function shopifyProductUrl(productId: number | string | null | undefined): string | null {
  if (!SHOPIFY_SHOP || productId == null || productId === '') return null
  return `https://${SHOPIFY_SHOP}/admin/products/${productId}`
}

// ── Formatters ───────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined, suffix = ''): string {
  if (n == null) return '—'
  return Math.round(n) + suffix
}
function fmtDec(n: number | null | undefined, dp = 2): string {
  if (n == null) return '—'
  return n.toFixed(dp)
}
function fmtCov(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n >= 999) return '∞'
  return Math.round(n).toString()
}
function fmtCost(n: number | null | undefined): string {
  if (n == null) return '—'
  return '$' + Math.round(n).toLocaleString()
}
function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—'
  return (n * 100).toFixed(1) + '%'
}

// ── Column metadata ──────────────────────────────────────────────────────────

const NUM_COLS = new Set([
  'on_hand', 'incoming', 'recommended_qty', 'days_coverage',
  'qty_sold_30d', 'qty_sold_60d', 'qty_sold_90d', 'qty_sold_120d',
  'last_in_stock_60d', 'in_stock_days_60', 'true_velocity', 'lifetime_rate_monthly', 'recent_vs_lifetime',
  'product_lifetime_rate_monthly', 'product_recent_vs_lifetime',
  'seasonal_multiplier',
  'predicted_demand', 'age_days', 'return_rate', 'marketing_multiplier',
  'unit_cost', 'cost_of_order_dollars',
])

// Default widths in px — also used by colgroup. Each non-trivial column is wide
// enough that its full header label is visible by default (the header row also
// carries a drag grip, sort icon and pin button, so allow ~54px of chrome on
// top of the label text). Users can still drag columns narrower than this.
const COL_WIDTHS: Record<string, number> = {
  signal_stripe: 4,
  select: 18,
  product_title: 320,
  barcode: 130,
  sku: 110,
  variant_title: 110,
  product_type: 140,
  vendor: 130,
  status: 96,
  on_hand: 88,
  incoming: 108,
  action_signal: 140,
  recommended_qty: 104,
  qty_sold_30d: 104,
  qty_sold_60d: 104,
  qty_sold_90d: 104,
  qty_sold_120d: 112,
  last_in_stock_60d: 150,
  in_stock_days_60: 156,
  true_velocity: 132,
  lifetime_rate_monthly: 130,
  recent_vs_lifetime: 130,
  product_lifetime_rate_monthly: 190,
  product_recent_vs_lifetime: 170,
  lifetime_spike: 140,
  lifetime_spike_all: 170,
  seasonal_multiplier: 150,
  predicted_demand: 152,
  days_coverage: 134,
  age_days: 116,
  return_rate: 124,
  marketing_multiplier: 150,
  unit_cost: 112,
  cost_of_order_dollars: 138,
}

// User-facing labels for the column-settings menu.
const COL_LABELS: Record<string, string> = {
  product_title: 'Product',
  barcode: 'Barcode',
  sku: 'SKU',
  variant_title: 'Variant',
  product_type: 'Product Type',
  vendor: 'Vendor',
  status: 'Status',
  on_hand: 'Stock',
  incoming: 'Incoming',
  action_signal: 'Action Signal',
  recommended_qty: 'Rec Qty',
  qty_sold_30d: '30D Sold',
  qty_sold_60d: '60D Sold',
  qty_sold_90d: '90D Sold',
  qty_sold_120d: '120D Sold',
  last_in_stock_60d: 'Last In Stock 60D',
  in_stock_days_60: 'In-stock days (60)',
  true_velocity: 'True Velocity',
  lifetime_rate_monthly: 'Usual pace',
  recent_vs_lifetime: 'vs usual',
  product_lifetime_rate_monthly: 'Product usual pace',
  product_recent_vs_lifetime: 'Product vs usual',
  lifetime_spike: 'Check history',
  lifetime_spike_all: 'Check history (all)',
  seasonal_multiplier: 'Seasonal Lookup',
  predicted_demand: 'Predicted Demand',
  days_coverage: 'Days Coverage',
  age_days: 'Age (Days)',
  return_rate: 'Return Rate',
  marketing_multiplier: 'Mktg Multiplier',
  unit_cost: 'Unit Cost',
  cost_of_order_dollars: 'Cost of Order',
}

// Lifetime columns: how good the availability data is (A1 reading rules).
const AVAILABILITY_NOTE =
  'Availability before July 2026 is approximate (from Shopify\'s history); from July 2026 it is exact. ' +
  'June 2026 is left out (its days and its sales): Shopify\'s history for it is unreliable.'
// Per cell, when some months had no availability data and were counted as
// calendar days instead (backfill not loaded).
const CALENDAR_FALLBACK_NOTE =
  'Some months had no availability data, so they were counted as if available all month.'

function lifetimeCell(text: string | null, fallback: boolean | undefined) {
  if (text == null) return <span style={{ color: 'var(--mute)' }}>—</span>
  return (
    <span className="tabular-nums" title={fallback ? CALENDAR_FALLBACK_NOTE : undefined}>
      {text}
    </span>
  )
}

// Hover-tooltip explanations. Every column except Product, Variant,
// Product Type, Vendor, Stock, and Incoming gets one — even non-formula
// columns get a one-liner explaining what the value represents.
const COL_TOOLTIPS: Record<string, string> = {
  barcode:
    'Shopify variant barcode (UPC/EAN/ISBN). Used as the primary scan key in Receiving and to match incoming PO line items back to variants.',
  sku:
    'Internal Stock Keeping Unit assigned by Shopify. Used as a secondary fallback identifier when a barcode is missing.',
  status:
    'Shopify product status: active, draft, or archived. Archived items are excluded from most calculations.',
  action_signal:
    'Computed recommendation derived from tags, return rate, stock, and predicted demand.\n' +
    '• ORDER NOW — stock + incoming covers fewer days than lead time + buffer days, and Rec Qty is at least 1 → buy more\n' +
    '• STOP — tagged CLEARANCE or DISCONTINUED in Shopify: not being restocked, so Rec Qty is 0.\n' +
    '• HIGH RETURNS — returns over 15% of net sales in the last 60 days; check before reordering\n' +
    '• LIQUIDATE — Stock isn\'t moving.\n' +
    '• OK — stock + incoming covers lead time + buffer days (or the shortfall rounds to 0 units)\n' +
    '• NO ACTION — over 90 days old, no net sales in 60 days, no predicted demand, and nothing on hand or incoming',
  recommended_qty:
    'Suggested order quantity to bring inventory back to target coverage.\n' +
    'Rec Qty = ceil( predicted_demand × (lead_time + buffer_days) ) − on_hand − incoming\n' +
    'Clamped at 0; respects vendor MOV when batching into a PO.\n' +
    'Always 0 for archived products and the inv_exclude tag.',
  qty_sold_30d: 'Units sold over the last 30 days (Shopify order line items, refunds netted out).',
  qty_sold_60d: 'Units sold over the last 60 days (Shopify order line items, refunds netted out).',
  qty_sold_90d: 'Units sold over the last 90 days (Shopify order line items, refunds netted out).',
  qty_sold_120d: 'Units sold over the last 120 days (Shopify order line items, refunds netted out).',
  last_in_stock_60d:
    'Units sold in the most recent month that had sales, plus the month before it (from monthly sales history).\n' +
    '"None" = no month with sales on record. Display only: True Velocity uses In-stock days (60) instead.',
  in_stock_days_60: 'Days in the last 60 it had stock or sold — what True Velocity is based on.',
  true_velocity:
    'Units per day over the last 60 days (30 if newer), counting only days it had stock or sold.\n' +
    'With 3 or fewer sales in 60 days and 120+ available days, never above its usual pace.',
  lifetime_rate_monthly:
    'Average units this size sold per month while it was available (in stock or selling), since it was first stocked (or since July 2024).\n' +
    'Months it was sold out don\'t count, so a size that is often out of stock doesn\'t read low. It caps True Velocity for slow sellers (see True Velocity).\n' +
    AVAILABILITY_NOTE,
  recent_vs_lifetime:
    'This size\'s recent selling pace compared with its usual pace. 5.0× usual = selling five times as fast as usual.\n' +
    'Blank when it has no sales yet, or hasn\'t been available long enough (minimum set in Admin → Settings). Display only.\n' +
    AVAILABILITY_NOTE,
  product_lifetime_rate_monthly:
    'Usual pace of the whole product: all its sizes together, per month the product was available (any size in stock or selling).\n' +
    'Colours, styles and other non-size options are kept apart: each has its own figure. Blank until a full sync has read the product\'s options. Display only.\n' +
    AVAILABILITY_NOTE,
  product_recent_vs_lifetime:
    'The whole product\'s recent selling pace (all its sizes) compared with its usual pace. 5.0× usual = five times as fast as usual.\n' +
    'Blank when it has no sales yet, or hasn\'t been available long enough (minimum set in Admin → Settings). Display only.\n' +
    AVAILABILITY_NOTE,
  lifetime_spike:
    'The product (all its sizes, per colour or style) is selling well above its usual pace on very few units — check its history before reordering.\n' +
    'Shows Check when the product is selling at least K× usual, sold at most N (product) units in the last 60 days, and has been available at least M days ' +
    '(K, N, M in Admin → Settings). A real breakout has many sales and isn\'t marked.\n' +
    'Only shown when a reorder is suggested (Rec Qty 1 or more).\n' +
    'Display only: it does not change Rec Qty, signals or any forecast.',
  lifetime_spike_all:
    'Product flag before hiding rows with no reorder suggested — for reviewing the K, N, M thresholds.',
  seasonal_multiplier:
    'Units this size sold last year in this month and the next two.\n' +
    'Counts toward demand only at 2 units or more.',
  predicted_demand:
    'Forward-looking daily demand forecast for the next ordering window.\n' +
    'predicted_demand = max(true_velocity, seasonal_lookup / 91) × marketing_multiplier\n' +
    '(seasonal lookup only at 2 units or more)\n' +
    '(units / day, 3 decimals).',
  days_coverage:
    'How many days the current stock-on-hand will last at predicted demand.\n' +
    'days_coverage = on_hand / predicted_demand\n' +
    '"∞" shown when predicted_demand is 0; rounded to whole days.',
  age_days:
    'Days since this variant was first created in Shopify. Useful for spotting new vs. aged inventory.',
  return_rate:
    'Fraction of units sold in the last 120 days that were refunded/returned.\n' +
    'return_rate = units_refunded_120d / qty_sold_120d',
  marketing_multiplier:
    'Manual demand-lift override per product type (set in Admin → Settings). ' +
    '1.00 = neutral; 1.20 = boost demand by 20% (e.g. during a promo).',
  unit_cost:
    'Landed unit cost in USD. Pulled from Shopify InventoryItem.cost, then padded ' +
    'by vendor duty% + freight% when computing PO order totals.',
  cost_of_order_dollars:
    'Estimated USD cost to order the recommended quantity at the current unit cost.\n' +
    'Cost of Order = recommended_qty × unit_cost × (1 + duty_pct + freight_pct)',
}

// Columns the user can toggle from the Columns dropdown (skip signal/select).
const TOGGLEABLE_COLUMNS: { id: string; label: string }[] = Object.entries(COL_LABELS).map(
  ([id, label]) => ({ id, label })
)

// ── Per-Shopify-location stock columns ───────────────────────────────────────
//
// One column per row in `shopify_locations`, built at runtime from
// GET /api/inventory/locations — never hardcoded, so a location added in
// Shopify appears after the next sync. They sit immediately left of Stock
// (the cross-location total) and are HIDDEN BY DEFAULT: `columnVisibility`
// only carries an entry once the user toggles one, so the default grid keeps
// its existing width.

const LOCATION_COL_PREFIX = 'loc_'
const LOCATION_COL_WIDTH = 150

function locationColumnId(locationId: number): string {
  return `${LOCATION_COL_PREFIX}${locationId}`
}
function isLocationColumnId(id: string): boolean {
  return id.startsWith(LOCATION_COL_PREFIX)
}
function colWidth(id: string): number {
  return COL_WIDTHS[id] ?? (isLocationColumnId(id) ? LOCATION_COL_WIDTH : 80)
}

// ── Column definitions ───────────────────────────────────────────────────────

function buildColumns(
  selectedIds: Set<string>,
  allIds: string[],
  toggleSelected: (id: string) => void,
  toggleSelectAll: (ids: string[]) => void,
  locations: InventoryLocation[]
): ColumnDef<DashboardRow>[] {
  const cols: ColumnDef<DashboardRow>[] = [
    {
      id: 'signal_stripe',
      header: '',
      cell: ({ row }) => {
        const kind = signalKind(row.original.action_signal)
        return <div className={`sig-${kind}`} style={{ minHeight: 22 }} />
      },
      enableSorting: false,
      enableHiding: false,
      size: COL_WIDTHS.signal_stripe,
    },
    {
      id: 'select',
      header: () => {
        const allSel = allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
        return (
          <input
            type="checkbox"
            aria-label="Select all"
            checked={allSel}
            onChange={() => toggleSelectAll(allIds)}
            style={{ width: 12, height: 12 }}
          />
        )
      },
      cell: ({ row }) => {
        const id = String(row.original.variant_id ?? '')
        return (
          <input
            type="checkbox"
            aria-label={`Select ${row.original.product_title}`}
            checked={selectedIds.has(id)}
            onChange={() => toggleSelected(id)}
            style={{ width: 12, height: 12 }}
          />
        )
      },
      enableSorting: false,
      enableHiding: false,
      size: COL_WIDTHS.select,
    },
    {
      id: 'product_title',
      accessorKey: 'product_title',
      header: 'Product',
      cell: ({ row }) => {
        const title = row.original.product_title ?? ''
        const url = shopifyProductUrl(row.original.product_id as number | null | undefined)
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="dash-link"
              onClick={(e) => e.stopPropagation()}
              title={`Open "${title}" in Shopify`}
            >
              {title}
            </a>
          )
        }
        return <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{title}</span>
      },
    },
    {
      id: 'barcode',
      accessorKey: 'barcode',
      header: 'Barcode',
      cell: ({ getValue }) => {
        const v = String(getValue() ?? '')
        return v
          ? <span className="tabular-nums" style={{ color: 'var(--ink-2)' }}>{v}</span>
          : <span style={{ color: 'var(--mute)' }}>—</span>
      },
    },
    {
      id: 'sku',
      accessorKey: 'sku',
      header: 'SKU',
      cell: ({ getValue }) => {
        const v = String(getValue() ?? '')
        return v
          ? <span style={{ color: 'var(--ink-2)' }}>{v}</span>
          : <span style={{ color: 'var(--mute)' }}>—</span>
      },
    },
    {
      id: 'variant_title',
      accessorKey: 'variant_title',
      header: 'Variant',
      cell: ({ getValue }) => (
        <span style={{ color: 'var(--ink-2)' }}>{String(getValue() ?? '—')}</span>
      ),
    },
    {
      id: 'product_type',
      accessorKey: 'product_type',
      header: 'Product Type',
      cell: ({ getValue }) => (
        <span style={{ color: 'var(--ink-2)' }}>{String(getValue() ?? '—')}</span>
      ),
    },
    {
      id: 'vendor',
      accessorKey: 'vendor',
      header: 'Vendor',
      cell: ({ getValue }) => (
        <span style={{ color: 'var(--ink-2)' }}>{String(getValue() ?? '—')}</span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const v = String(getValue() ?? '—')
        return <span style={{ color: 'var(--mute)', textTransform: 'capitalize' }}>{v}</span>
      },
    },
    {
      id: 'on_hand',
      accessorKey: 'on_hand',
      header: 'Stock',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'incoming',
      accessorKey: 'incoming',
      header: 'Incoming',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'action_signal',
      accessorKey: 'action_signal',
      header: 'Action Signal',
      cell: ({ row }) => {
        const kind = signalKind(row.original.action_signal)
        const label = SIGNAL_LABELS[kind]
        return <span className={`dash-siglabel sig-${kind}`}>{label}</span>
      },
    },
    {
      id: 'recommended_qty',
      accessorKey: 'recommended_qty',
      header: 'Rec Qty',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'qty_sold_30d',
      accessorKey: 'qty_sold_30d',
      header: '30D Sold',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'qty_sold_60d',
      accessorKey: 'qty_sold_60d',
      header: '60D Sold',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'qty_sold_90d',
      accessorKey: 'qty_sold_90d',
      header: '90D Sold',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'qty_sold_120d',
      accessorKey: 'qty_sold_120d',
      header: '120D Sold',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'last_in_stock_60d',
      accessorKey: 'last_in_stock_60d',
      header: 'Last In Stock 60D',
      cell: ({ getValue }) => {
        const n = getValue() as number | null | undefined
        if (n == null) return <span className="tabular-nums" style={{ color: 'var(--mute)' }}>None</span>
        return <span className="tabular-nums">{Math.round(n)}</span>
      },
    },
    {
      id: 'in_stock_days_60',
      accessorKey: 'in_stock_days_60',
      header: 'In-stock days (60)',
      cell: ({ getValue }) => {
        const n = getValue() as number | null | undefined
        if (n == null) return <span style={{ color: 'var(--mute)' }}>—</span>
        return <span className="tabular-nums">{n} of 60</span>
      },
    },
    {
      id: 'true_velocity',
      accessorKey: 'true_velocity',
      header: 'True Velocity',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtDec(getValue() as number, 3)}</span>,
    },
    {
      id: 'lifetime_rate_monthly',
      accessorKey: 'lifetime_rate_monthly',
      header: 'Usual pace',
      cell: ({ getValue, row }) => {
        const n = getValue() as number | null | undefined
        return lifetimeCell(n == null ? null : fmtUsualPace(n), row.original.lifetime_calendar_fallback)
      },
    },
    {
      id: 'recent_vs_lifetime',
      accessorKey: 'recent_vs_lifetime',
      header: 'vs usual',
      cell: ({ getValue, row }) => {
        const n = getValue() as number | null | undefined
        return lifetimeCell(n == null ? null : fmtVsUsual(n), row.original.lifetime_calendar_fallback)
      },
    },
    {
      id: 'product_lifetime_rate_monthly',
      accessorKey: 'product_lifetime_rate_monthly',
      header: 'Product usual pace',
      cell: ({ getValue, row }) => {
        const n = getValue() as number | null | undefined
        return lifetimeCell(n == null ? null : fmtUsualPace(n), row.original.product_lifetime_calendar_fallback)
      },
    },
    {
      id: 'product_recent_vs_lifetime',
      accessorKey: 'product_recent_vs_lifetime',
      header: 'Product vs usual',
      cell: ({ getValue, row }) => {
        const n = getValue() as number | null | undefined
        return lifetimeCell(n == null ? null : fmtVsUsual(n), row.original.product_lifetime_calendar_fallback)
      },
    },
    {
      // Column id kept (saved layouts); shows the PRODUCT-level flag (L1b).
      id: 'lifetime_spike',
      accessorKey: 'product_lifetime_spike',
      header: 'Check history',
      cell: ({ getValue }) =>
        getValue()
          ? <span style={{ color: 'var(--ink-2)' }}>Check</span>
          : <span style={{ color: 'var(--mute)' }}>—</span>,
    },
    {
      // The same product flag on every row, Rec Qty 0 included (threshold tuning).
      id: 'lifetime_spike_all',
      accessorKey: 'product_lifetime_spike_all',
      header: 'Check history (all)',
      cell: ({ getValue }) =>
        getValue()
          ? <span style={{ color: 'var(--ink-2)' }}>Check</span>
          : <span style={{ color: 'var(--mute)' }}>—</span>,
    },
    {
      id: 'seasonal_multiplier',
      accessorKey: 'seasonal_multiplier',
      header: 'Seasonal Lookup',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtDec(getValue() as number)}</span>,
    },
    {
      id: 'predicted_demand',
      accessorKey: 'predicted_demand',
      header: 'Predicted Demand',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtDec(getValue() as number, 3)}</span>,
    },
    {
      id: 'days_coverage',
      accessorKey: 'days_coverage',
      header: 'Days Coverage',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtCov(getValue() as number)}</span>,
    },
    {
      id: 'age_days',
      accessorKey: 'age_days',
      header: 'Age (Days)',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    },
    {
      id: 'return_rate',
      accessorKey: 'return_rate',
      header: 'Return Rate',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtPct(getValue() as number)}</span>,
    },
    {
      id: 'marketing_multiplier',
      accessorKey: 'marketing_multiplier',
      header: 'Mktg Multiplier',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtDec(getValue() as number)}</span>,
    },
    {
      id: 'unit_cost',
      accessorKey: 'unit_cost',
      header: 'Unit Cost',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtCost(getValue() as number)}</span>,
    },
    {
      id: 'cost_of_order_dollars',
      accessorKey: 'cost_of_order_dollars',
      header: 'Cost of Order',
      cell: ({ getValue }) => <span className="tabular-nums">{fmtCost(getValue() as number)}</span>,
    },
  ]

  // Per-location stock, inserted immediately left of the Stock total. Values
  // come from `stock_by_location`, keyed by location_id — the backend omits
  // zero-stock locations, so a missing key means 0 units there.
  const locationCols: ColumnDef<DashboardRow>[] = locations.map((loc) => {
    const key = String(loc.location_id)
    return {
      id: locationColumnId(loc.location_id),
      accessorFn: (r: DashboardRow) => r.stock_by_location?.[key] ?? 0,
      header: loc.name,
      cell: ({ getValue }) => <span className="tabular-nums">{fmtNum(getValue() as number)}</span>,
    }
  })
  if (locationCols.length > 0) {
    const stockIdx = cols.findIndex((c) => c.id === 'on_hand')
    cols.splice(stockIdx >= 0 ? stockIdx : cols.length, 0, ...locationCols)
  }

  // Seed every column's default width from COL_WIDTHS so TanStack's resize
  // math has a baseline; the signal stripe + select checkbox stay fixed.
  return cols.map((c) => {
    const id = c.id as string
    const fixed = id === 'signal_stripe' || id === 'select'
    return {
      ...c,
      size: colWidth(id),
      minSize: 28,
      enableResizing: !fixed,
    }
  })
}

// ── Column settings dropdown ──────────────────────────────────────────────────

export function DashboardColumnSettings() {
  const { columnVisibility, setColumnVisible, resetColumnLayout } = useDashboardStore()
  const locations = useInventoryLocations()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[var(--ink-2)]" aria-label="Column settings">
          <Settings className="h-3 w-3 mr-1" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] w-56 max-h-[60vh] overflow-y-auto">
        <DropdownMenuLabel className="text-xs font-semibold text-[var(--mute)] uppercase tracking-[0.06em]">
          Columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TOGGLEABLE_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={columnVisibility[col.id] ?? true}
            onCheckedChange={(v) => setColumnVisible(col.id, !!v)}
            onSelect={(e) => e.preventDefault()}
            className="text-xs"
          >
            {col.label}
          </DropdownMenuCheckboxItem>
        ))}
        {locations.map((loc) => {
          const id = locationColumnId(loc.location_id)
          return (
            <DropdownMenuCheckboxItem
              key={id}
              // Off unless the user has explicitly turned it on.
              checked={columnVisibility[id] ?? false}
              onCheckedChange={(v) => setColumnVisible(id, !!v)}
              onSelect={(e) => e.preventDefault()}
              className="text-xs"
            >
              {loc.name}
            </DropdownMenuCheckboxItem>
          )
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => resetColumnLayout()}
          className="text-xs text-[var(--ink-2)]"
        >
          <RotateCcw className="h-3 w-3 mr-1.5" />
          Reset layout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Sort icon ─────────────────────────────────────────────────────────────────

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-60" />
  if (sorted === 'desc') return <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-60" />
  return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-30" />
}

// ── Draggable header cell ─────────────────────────────────────────────────────

interface DraggableHeaderProps {
  header: Header<DashboardRow, unknown>
  canDrag: boolean
  pinned: boolean
  onTogglePin: (id: string) => void
  label: string
}

// Inner content of a header cell — wrapped by a <th> in the parent table.
function DraggableHeader({ header, canDrag, pinned, onTogglePin, label }: DraggableHeaderProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: header.column.id,
    disabled: !canDrag,
  })

  const canSort = header.column.getCanSort()
  const sorted = header.column.getIsSorted()
  const colId = header.column.id
  const isSignalStripe = colId === 'signal_stripe'
  const isSelect = colId === 'select'
  // Same test the body cells use, so a numeric header sits over its digits.
  const isNum = NUM_COLS.has(colId) || isLocationColumnId(colId)

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? 'none' : 'transform 150ms ease',
    opacity: isDragging ? 0.6 : 1,
  }

  if (isSignalStripe || isSelect) {
    return (
      <div ref={setNodeRef} className={cn('dash-th', isSelect && 'dash-th-select')} style={style}>
        {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      className={cn('dash-th', pinned && 'dash-th-pinned', isDragging && 'dash-th-dragging')}
      style={style}
    >
      <div className="dash-th-row">
        <span
          className="dash-th-grip"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
          aria-label={`Reorder ${label} column`}
        >
          ⋮⋮
        </span>
        {COL_TOOLTIPS[colId] ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="dash-th-label"
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                style={{ cursor: canSort ? 'pointer' : 'default', textAlign: isNum ? 'right' : undefined }}
              >
                {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}
                {canSort && <SortIcon sorted={sorted} />}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm whitespace-pre-line text-left">
              {COL_TOOLTIPS[colId]}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span
            className="dash-th-label"
            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
            style={{ cursor: canSort ? 'pointer' : 'default', textAlign: isNum ? 'right' : undefined }}
          >
            {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}
            {canSort && <SortIcon sorted={sorted} />}
          </span>
        )}
        <button
          type="button"
          className="dash-th-pin"
          onClick={(e) => {
            e.stopPropagation()
            onTogglePin(colId)
          }}
          title={pinned ? 'Unpin column' : 'Pin column to left'}
          aria-label={pinned ? `Unpin ${colId}` : `Pin ${colId} to left`}
        >
          {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
        </button>
      </div>
    </div>
  )
}

// ── Pinned-cell sticky offset helpers ─────────────────────────────────────────
//
// TanStack reports per-cell pinned state, but we need to compute the cumulative
// left offset of pinned columns ourselves (sum of widths of all preceding
// pinned cols). We compute it once per render based on the visible left-pinned
// list and the COL_WIDTHS map.

function leftOffsets(leftPinned: string[], widthOf: (id: string) => number): Record<string, number> {
  let acc = 0
  const offsets: Record<string, number> = {}
  for (const id of leftPinned) {
    offsets[id] = acc
    acc += widthOf(id)
  }
  return offsets
}

// ── Main component ────────────────────────────────────────────────────────────

interface DashboardTableProps {
  rows: DashboardRow[]
  sorting: SortingState
  onSortingChange: React.Dispatch<React.SetStateAction<SortingState>>
  onOpenCreatePO: () => void
}

function DashboardTableImpl({ rows, sorting, onSortingChange, onOpenCreatePO }: DashboardTableProps) {
  const locations = useInventoryLocations()
  const {
    selectedRowIds,
    toggleSelected,
    toggleSelectAll,
    columnVisibility,
    columnOrder,
    columnPinning,
    columnSizing,
    setColumnOrder,
    togglePinned,
    setColumnSizing,
  } = useDashboardStore()
  const [focusedRowIndex, setFocusedRowIndex] = React.useState<number | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const allIds = React.useMemo(() => rows.map((r) => String(r.variant_id ?? '')), [rows])

  const columns = React.useMemo(
    () => buildColumns(selectedRowIds, allIds, toggleSelected, toggleSelectAll, locations),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowIds, allIds, locations]
  )

  const locationColumnIds = React.useMemo(
    () => locations.map((l) => locationColumnId(l.location_id)),
    [locations]
  )

  // Location column labels for the header grip / resize handle aria-labels.
  const labelOf = React.useCallback(
    (id: string) =>
      COL_LABELS[id] ??
      locations.find((l) => locationColumnId(l.location_id) === id)?.name ??
      id,
    [locations]
  )

  const tanstackVisibility: VisibilityState = React.useMemo(() => {
    const v: VisibilityState = {}
    for (const [k, visible] of Object.entries(columnVisibility)) {
      if (k !== 'signal_stripe' && k !== 'select') v[k] = visible
    }
    // Location columns are opt-in: TanStack defaults unknown columns to
    // visible, so they need an explicit false until the user toggles one on.
    for (const id of locationColumnIds) v[id] = columnVisibility[id] ?? false
    return v
  }, [columnVisibility, locationColumnIds])

  // Location columns can't live in the persisted default order (their ids
  // depend on the Shopify location ids), so splice them in here: drop ids for
  // locations that no longer exist, and insert any new ones immediately left
  // of Stock. Once the user drags one, its position is persisted like any
  // other column and preserved on the next load.
  const effectiveColumnOrder = React.useMemo(() => {
    const known = new Set(locationColumnIds)
    const base = columnOrder.filter((id) => !isLocationColumnId(id) || known.has(id))
    const missing = locationColumnIds.filter((id) => !base.includes(id))
    if (missing.length === 0) return base
    const stockIdx = base.indexOf('on_hand')
    const at = stockIdx >= 0 ? stockIdx : base.length
    return [...base.slice(0, at), ...missing, ...base.slice(at)]
  }, [columnOrder, locationColumnIds])

  const tanstackPinning: ColumnPinningState = React.useMemo(
    () => ({ left: columnPinning.left, right: [] }),
    [columnPinning]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnVisibility: tanstackVisibility,
      columnOrder: effectiveColumnOrder,
      columnPinning: tanstackPinning,
      columnSizing,
    },
    onSortingChange: onSortingChange as OnChangeFn<SortingState>,
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(effectiveColumnOrder) : updater
      setColumnOrder(next)
    },
    onColumnSizingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnSizing) : updater
      setColumnSizing(next)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnPinning: true,
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
  })

  // Keyboard navigation
  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    function onKeyDown(e: KeyboardEvent) {
      const tag = (document.activeElement as HTMLElement | null)?.tagName?.toUpperCase()
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setFocusedRowIndex((prev) => Math.min((prev ?? -1) + 1, rows.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setFocusedRowIndex((prev) => Math.max((prev ?? 1) - 1, 0))
      } else if (e.key === ' ' && !inInput) {
        e.preventDefault()
        if (focusedRowIndex != null) {
          const r = table.getRowModel().rows[focusedRowIndex]
          if (r) toggleSelected(String(r.original.variant_id ?? ''))
        }
      } else if (e.key === 'Enter' && !inInput) {
        e.preventDefault()
        toast('Variant detail coming in a later session')
      } else if ((e.key === 'c' || e.key === 'C') && !inInput) {
        e.preventDefault()
        onOpenCreatePO()
      }
    }
    el.addEventListener('keydown', onKeyDown)
    return () => el.removeEventListener('keydown', onKeyDown)
  }, [rows, focusedRowIndex, toggleSelected, onOpenCreatePO, table])

  // ── dnd-kit setup ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  )

  // Sortable items = headers in the order TanStack would render them, EXCLUDING
  // the non-draggable signal_stripe + select.
  const sortableIds = React.useMemo(() => {
    const allHeaders = table.getHeaderGroups()[0]?.headers ?? []
    return allHeaders.map((h) => h.column.id).filter((id) => id !== 'signal_stripe' && id !== 'select')
  }, [table])

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIdx = effectiveColumnOrder.indexOf(active.id as string)
    const newIdx = effectiveColumnOrder.indexOf(over.id as string)
    if (oldIdx < 0 || newIdx < 0) return
    setColumnOrder(arrayMove(effectiveColumnOrder, oldIdx, newIdx))
  }

  const tableRows = table.getRowModel().rows
  const headers = table.getHeaderGroups()[0]?.headers ?? []
  // Drive the table width from the exact pixel sum of column widths rather than
  // `max-content`, so shrinking a column past its text content actually works
  // (cells clip via overflow:hidden) instead of the content pinning the width.
  const totalWidth = headers.reduce((sum, h) => sum + h.getSize(), 0)
  const offsets = React.useMemo(
    () => leftOffsets(columnPinning.left, (id) => columnSizing[id] ?? colWidth(id)),
    [columnPinning.left, columnSizing]
  )

  // ── Row virtualization ──
  // At 30k+ rows, rendering every <tr> freezes the tab. Only the visible
  // ~30 rows go in the DOM; the rest are simulated via two spacer <tr>s
  // above and below that pad the tbody to the correct total height.
  const ROW_HEIGHT = 26 // px — matches dash-td padding (4×2) + 12px font × 1.3 line + 1px border
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  })
  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0

  // Keep keyboard-focused row visible
  React.useEffect(() => {
    if (focusedRowIndex != null) {
      rowVirtualizer.scrollToIndex(focusedRowIndex, { align: 'auto' })
    }
  }, [focusedRowIndex, rowVirtualizer])

  // Helper: get sticky style for a pinned cell.
  function pinStyle(colId: string): React.CSSProperties {
    if (!columnPinning.left.includes(colId)) return {}
    return {
      position: 'sticky',
      left: offsets[colId] ?? 0,
      zIndex: 2,
      background: 'inherit',
    }
  }

  return (
    // delayDuration=150ms — faster than the browser-native title attribute
    // (~500–1500ms depending on OS) so column-header hints surface almost
    // immediately while still avoiding flicker on accidental hovers.
    <TooltipProvider delayDuration={150}>
    <div
      ref={containerRef}
      className="dash-table-wrap"
      tabIndex={0}
      role="region"
      aria-label="Inventory dashboard"
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <table className="dash-table" style={{ width: totalWidth }}>
          <colgroup>
            {headers.map((h) => (
              <col key={h.id} style={{ width: h.getSize() }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                {headers.map((header) => {
                  const colId = header.column.id
                  const pinned = columnPinning.left.includes(colId)
                  const canDrag = colId !== 'signal_stripe' && colId !== 'select'
                  return (
                    <th
                      key={header.id}
                      style={{
                        ...pinStyle(colId),
                        background: 'var(--surface-header)',
                        zIndex: pinned ? 3 : 2,
                      }}
                      className="dash-th-sticky"
                    >
                      <DraggableHeader
                        header={header}
                        canDrag={canDrag}
                        pinned={pinned}
                        onTogglePin={togglePinned}
                        label={labelOf(colId)}
                      />
                      {header.column.getCanResize() && (
                        <div
                          className={cn('dash-th-resize', header.column.getIsResizing() && 'resizing')}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(e) => e.stopPropagation()}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${labelOf(colId)} column`}
                          // onEnd mode: the column width only commits on release, so
                          // translate the handle by the live drag delta to give the
                          // user a visible indicator of where the new edge will land.
                          style={
                            header.column.getIsResizing()
                              ? { transform: `translateX(${table.getState().columnSizingInfo.deltaOffset ?? 0}px)` }
                              : undefined
                          }
                        />
                      )}
                    </th>
                  )
                })}
              </SortableContext>
            </tr>
          </thead>
          <tbody>
            {tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{ padding: '40px 16px', textAlign: 'center', fontSize: 13, color: 'var(--mute)' }}
                >
                  No items match the current filters
                </td>
              </tr>
            ) : (
              <>
                {paddingTop > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={headers.length} style={{ height: paddingTop, padding: 0, border: 0 }} />
                  </tr>
                )}
                {virtualItems.map((vi) => {
                  const row = tableRows[vi.index]
                  const rowIdx = vi.index
                  const isFocused = focusedRowIndex === rowIdx
                  // No zebra: every row sits on the card surface, because striping
                  // measurably cuts the contrast of the signal stripes and labels.
                  // Focus is a selection state, so the warm header tint (not amber).
                  const rowBg = isFocused ? 'var(--surface-header)' : 'var(--surface-card)'
                  return (
                    <tr
                      key={row.id}
                      className={cn('dash-tr', isFocused && 'dash-row-focused')}
                      onClick={() => setFocusedRowIndex(rowIdx)}
                      style={{ background: rowBg, height: ROW_HEIGHT }}
                    >
                      {row.getVisibleCells().map((cell: Cell<DashboardRow, unknown>) => {
                        const colId = cell.column.id
                        const isSignal = colId === 'signal_stripe'
                        const isSelect = colId === 'select'
                        const isNum = NUM_COLS.has(colId) || isLocationColumnId(colId)
                        const cellStyle: React.CSSProperties = isSignal
                          ? { padding: 0, width: 4, minWidth: 4 }
                          : {
                              textAlign: isNum ? 'right' : 'left',
                            }
                        return (
                          <td
                            key={cell.id}
                            className={cn('dash-td', isSignal && 'dash-sigcell', isSelect && 'dash-selcell')}
                            style={{
                              ...cellStyle,
                              ...pinStyle(colId),
                              background: columnPinning.left.includes(colId) ? rowBg : undefined,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={headers.length} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
    </TooltipProvider>
  )
}

export const DashboardTable = React.memo(DashboardTableImpl)
